/**
 * VÉRAULT TRAVELS — Auto-Plan Day Component
 * 
 * AI-powered day planning: combines Places + Directions APIs to suggest
 * optimized day itineraries for a destination.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, MapPin, Clock, ArrowDown, Coffee, Utensils,
  Landmark, TreePine, ChevronDown, ChevronUp, Navigation, Car, Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_TEMPLATES = [
  { id: 'culture', label: 'Culture & History', morning: 'museums', lunch: 'restaurants', afternoon: 'attractions' },
  { id: 'food', label: 'Foodie Tour', morning: 'cafes', lunch: 'restaurants', afternoon: 'markets' },
  { id: 'nature', label: 'Nature & Relaxation', morning: 'parks', lunch: 'cafes', afternoon: 'spas' },
  { id: 'nightlife', label: 'Evening Out', morning: 'shopping', lunch: 'restaurants', afternoon: 'bars' },
];

export function AutoPlanDay({ destination, onAddToItinerary }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [customDest, setCustomDest] = useState('');

  const generatePlan = async (template) => {
    const dest = customDest || destination;
    if (!dest) {
      toast.error('Enter a destination first');
      return;
    }
    setLoading(true);
    setPlan(null);

    try {
      // Fetch places for each time slot
      const [morningRes, lunchRes, afternoonRes] = await Promise.all([
        axios.get(`${API}/places/search`, { params: { destination: dest, category: template.morning, radius: 5000, limit: 3 } }),
        axios.get(`${API}/places/search`, { params: { destination: dest, category: template.lunch, radius: 5000, limit: 3 } }),
        axios.get(`${API}/places/search`, { params: { destination: dest, category: template.afternoon, radius: 5000, limit: 3 } }),
      ]);

      const morning = morningRes.data.places?.[0];
      const lunch = lunchRes.data.places?.[0];
      const afternoon = afternoonRes.data.places?.[0];

      if (!morning && !lunch && !afternoon) {
        toast.error('Not enough places found for this destination');
        setLoading(false);
        return;
      }

      // Calculate routes between the places
      const stops = [morning, lunch, afternoon].filter(Boolean);
      const routes = [];

      for (let i = 0; i < stops.length - 1; i++) {
        if (stops[i]?.lat && stops[i + 1]?.lat) {
          try {
            const routeRes = await axios.get(`${API}/directions/route`, {
              params: {
                origin_lat: stops[i].lat,
                origin_lon: stops[i].lon,
                dest_lat: stops[i + 1].lat,
                dest_lon: stops[i + 1].lon,
                mode: 'driving',
              },
            });
            routes.push(routeRes.data.routes?.[0] || null);
          } catch {
            routes.push(null);
          }
        } else {
          routes.push(null);
        }
      }

      setPlan({
        template: template.label,
        destination: dest,
        stops: [
          { time: 'Morning', place: morning, category: template.morning },
          { time: 'Lunch', place: lunch, category: template.lunch },
          { time: 'Afternoon', place: afternoon, category: template.afternoon },
        ],
        routes,
      });
    } catch (e) {
      toast.error('Failed to generate day plan');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (cat) => {
    const icons = { museums: Landmark, restaurants: Utensils, cafes: Coffee, parks: TreePine, attractions: Landmark, markets: MapPin, bars: Coffee, spas: Sparkles, shopping: MapPin };
    return icons[cat] || MapPin;
  };

  return (
    <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6" data-testid="auto-plan-day">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-4"
      >
        <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#C8A85A]" />
          Auto-Plan Day
        </h3>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#8C8C8C]" /> : <ChevronDown className="w-4 h-4 text-[#8C8C8C]" />}
      </button>

      {expanded && (
        <div className="space-y-4">
          <p className="text-sm text-[#8C8C8C]">
            Generate an optimized day itinerary with real places and travel times between them.
          </p>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
              <Input
                value={customDest}
                onChange={(e) => setCustomDest(e.target.value)}
                placeholder={destination || 'Destination...'}
                className="pl-10 bg-[#F5F1EB]"
                data-testid="autoplan-destination"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {DAY_TEMPLATES.map(t => (
              <button
                key={t.id}
                onClick={() => generatePlan(t)}
                disabled={loading}
                className="p-3 rounded-lg bg-[#F5F1EB] hover:bg-[#EEE9E2] text-left transition-colors"
                data-testid={`autoplan-template-${t.id}`}
              >
                <p className="text-sm font-medium text-[#1A1A1A]">{t.label}</p>
                <p className="text-xs text-[#8C8C8C] mt-0.5 capitalize">
                  {t.morning} &rarr; {t.lunch} &rarr; {t.afternoon}
                </p>
              </button>
            ))}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8 gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-[#C8A85A]" />
              <span className="text-sm text-[#8C8C8C]">Planning your perfect day...</span>
            </div>
          )}

          {plan && !loading && (
            <div className="space-y-1 pt-2 border-t border-[rgba(0,0,0,0.06)]" data-testid="autoplan-result">
              <p className="text-xs text-[#C8A85A] font-medium uppercase tracking-wide mb-3">
                {plan.template} in {plan.destination}
              </p>
              {plan.stops.map((stop, idx) => {
                const StopIcon = getCategoryIcon(stop.category);
                return (
                  <React.Fragment key={idx}>
                    <div className="flex items-start gap-3 p-3 bg-[#F9F8F4] rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-[#C8A85A]/10 flex items-center justify-center flex-shrink-0">
                        <StopIcon className="w-4 h-4 text-[#C8A85A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#C8A85A] font-medium uppercase">{stop.time}</p>
                        {stop.place ? (
                          <>
                            <p className="text-sm font-medium text-[#1A1A1A]">{stop.place.name}</p>
                            {stop.place.cuisine && <p className="text-xs text-[#8C8C8C]">{stop.place.cuisine.replace(/;/g, ', ')}</p>}
                            {stop.place.address && <p className="text-xs text-[#8C8C8C]">{stop.place.address}</p>}
                          </>
                        ) : (
                          <p className="text-sm text-[#8C8C8C] italic">No {stop.category} found nearby</p>
                        )}
                      </div>
                      {stop.place && onAddToItinerary && (
                        <button
                          onClick={() => { onAddToItinerary(stop.place); toast.success(`Added "${stop.place.name}"`); }}
                          className="p-1 rounded hover:bg-[#C8A85A]/10"
                          title="Add to itinerary"
                        >
                          <Plus className="w-4 h-4 text-[#C8A85A]" />
                        </button>
                      )}
                    </div>
                    {idx < plan.stops.length - 1 && plan.routes[idx] && (
                      <div className="flex items-center gap-2 ml-7 py-1">
                        <div className="w-px h-4 bg-[#C8A85A]/30"></div>
                        <Car className="w-3 h-3 text-[#8C8C8C]" />
                        <span className="text-xs text-[#8C8C8C]">
                          {plan.routes[idx].duration_text} ({plan.routes[idx].distance_text})
                        </span>
                      </div>
                    )}
                    {idx < plan.stops.length - 1 && !plan.routes[idx] && (
                      <div className="flex items-center gap-2 ml-7 py-1">
                        <div className="w-px h-4 bg-[#C8A85A]/30"></div>
                        <ArrowDown className="w-3 h-3 text-[#8C8C8C]" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              <p className="text-xs text-[#B0B0B0] mt-2">Real places + travel times. Source: OpenStreetMap + OSRM</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AutoPlanDay;
